/**
 * 支払条件(締め日・支払月・支払日・土日祝)の入力欄(2026-10-23)。
 * 取引先マスタの「支払条件」「入金条件」と、今後のドライバー報酬の共通設定で使う。
 * 入力の下に、今日の日付で計算した期日の例を出す(計算は terms.ts の dueDateFor)
 */
import { todayStr, weekdayLabel } from '@core/lib/date';
import type { DayOrEnd, HolidayShift, PaymentTerms } from '@core/lib/types';
import { HOLIDAY_SHIFT_OPTIONS, MONTH_OFFSET_OPTIONS, dueDateFor, validateTerms } from './terms';

const DAY_OPTIONS: Array<{ value: string; label: string }> = [
  ...Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: `${i + 1}日` })),
  { value: 'end', label: '末日' },
];

const DEFAULT_TERMS: PaymentTerms = { closing_day: 'end', month_offset: 1, pay_day: 'end', holiday_shift: 'after' };

function toDay(v: string): DayOrEnd {
  return v === 'end' ? 'end' : Number(v);
}

export default function TermsEditor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: PaymentTerms | null;
  onChange: (next: PaymentTerms | null) => void;
}) {
  const enabled = value !== null;
  const t = value ?? DEFAULT_TERMS;
  const invalid = enabled ? validateTerms(t) : null;
  const example = enabled && !invalid ? dueDateFor(todayStr(), t) : null;

  return (
    <div>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', width: 'auto' }}>
        <input
          type="checkbox"
          style={{ flexShrink: 0, margin: 0, width: 15, height: 15 }}
          checked={enabled}
          onChange={(e) => onChange(e.target.checked ? DEFAULT_TERMS : null)}
        />
        <span>{label}を設定する</span>
      </label>
      {enabled && (
        <>
          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', marginTop: 8 }}>
            <div>
              <label>締め日</label>
              <select
                value={String(t.closing_day)}
                onChange={(e) => onChange({ ...t, closing_day: toDay(e.target.value) })}
              >
                {DAY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>支払月</label>
              <select
                value={String(t.month_offset)}
                onChange={(e) => onChange({ ...t, month_offset: Number(e.target.value) as PaymentTerms['month_offset'] })}
              >
                {MONTH_OFFSET_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>支払日</label>
              <select value={String(t.pay_day)} onChange={(e) => onChange({ ...t, pay_day: toDay(e.target.value) })}>
                {DAY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>土日祝のとき</label>
              <select
                value={t.holiday_shift}
                onChange={(e) => onChange({ ...t, holiday_shift: e.target.value as HolidayShift })}
              >
                {HOLIDAY_SHIFT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {invalid ? (
            <p className="hint" style={{ color: 'var(--red)', margin: '6px 0 0' }}>
              {invalid}
            </p>
          ) : (
            example && (
              <p className="hint" style={{ margin: '6px 0 0' }}>
                例: 今日の分 → {example.closingDate}締め → 期日 {example.dueDate}{weekdayLabel(example.dueDate)}
              </p>
            )
          )}
        </>
      )}
    </div>
  );
}
